import {
  eraseCell,
  filledGrid,
  fullGridMistakes,
  isLocked,
  isLost,
  isSolved,
  placeDigit,
  reveal,
  toggleNote,
  undoMove,
  useHint as takeHint,
  type SudokuBoard as Board,
  type SudokuState,
} from "@bing/engine-sudoku";
import { withSudokuDefaults, type SudokuGame, type SudokuRecordKey } from "@bing/shared";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { ApiClient } from "../../api/client";
import { Button } from "../../components/Button";
import { EndCardShell } from "../../components/EndCardShell";
import { GameIcon } from "../../components/GameIcon";
import { faDigits } from "../../i18n/digits";
import { fa } from "../../i18n/fa";
import { lifecycle } from "../lifecycle";
import { formatPlayTime, SudokuClock } from "./clock";
import { editable, SudokuGrid, SudokuPad } from "./SudokuBoard";
import { SudokuSync, type KeptFinishes, type SudokuEnd } from "./sync";

/**
 * «سودوکو» — THE PLAY SCREEN: the band (exit · title · time), the grid, the pad, the solved card.
 *
 * ⚠️ ALMOST NO LOAD ON THE PHONE (CLAUDE.md). The time is exact — from timestamps (`SudokuClock`) —
 * and the screen repaints it once a second while the game is on screen and unsolved: ONE timer, and
 * none at all otherwise. Hidden (the document, or the app's fused activity signal: Telegram minimised,
 * a pagehide, a freeze), the clock pauses, the timer goes, and the game is saved at once (`keepalive`);
 * nothing runs until it is back. Back, ONE read picks up what another phone did meanwhile.
 * Moves are saved {@link SudokuSync}'s way: a few seconds after the last one — never a request a tap.
 * Leaving, the screen hands what it still owes the server (the last moves, or a finish waiting for
 * its answer) to `onClosing`, so the start screen reads the games only after it has landed.
 *
 * MISTAKE CHECKING (the operator, 2026-11-04) — the game's own mode, from its saved state: with it
 * ("check"), the counter sits in the tools row and the 3rd mistake ends the game at once (the lose
 * card; the loss is sent like a finish — kept on the device until the server answers). Without
 * ("free"), the first full grid with mistakes says how many (a card over the board, once per game —
 * saved), and «نشانم بده» marks them, once. Neither the counter nor the message ever moves or shrinks
 * the board: one sits in the tools row, the other over the screen.
 */

const EXIT_BTN =
  "appearance-none m-0 [font-family:inherit] cursor-pointer flex h-10 w-10 flex-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95";

/** The finish card's look — the solved, lost and full-grid cards alike. */
const END_CARD = {
  background: "var(--surface)",
  border: "1px solid var(--hairline)",
  borderRadius: "var(--radius-lg)",
  width: "100%",
  maxWidth: 320,
  textAlign: "center",
  boxShadow: "0 30px 70px rgba(0,0,0,.5)",
  gap: 16,
} as const;

/** The board's side when it cannot be measured (no layout engine): any sane size. */
const FALLBACK_SIDE = 320;

/** The band's «زمان» caption is `text-caption`, 12 px: what it computes to is 12 × the text zoom (an
 *  Android WebView's textZoom — ShelemTable's `textZoomOf`). */
const CAPTION_PX = 12;

/** The digit a key stands for by its PLACE on the keyboard (`event.code`: Digit1–9 on the top row,
 *  Numpad1–9) — whatever the layout writes there, the Persian one's ۱–۹ too; else 0. A numpad key is
 *  a digit only while it writes one: with NumLock off it is an arrow or Home/End (its `key` says so). */
function keyDigit(event: KeyboardEvent): number {
  const m = /^(Digit|Numpad)([1-9])$/.exec(event.code);
  if (!m) return 0;
  if (m[1] === "Numpad" && !/^\p{Nd}$/u.test(event.key)) return 0;
  return Number(m[2]);
}

/** The arrows' moves on the LTR grid: [rows, columns] — by `code`, and by `key` for a numpad key with
 *  NumLock off (its 8/2/4/6 are arrows then). */
const ARROWS: ReadonlyMap<string, readonly [number, number]> = new Map([
  ["ArrowUp", [-1, 0]],
  ["ArrowDown", [1, 0]],
  ["ArrowLeft", [0, -1]],
  ["ArrowRight", [0, 1]],
]);

/** A dialog is up (the solved card, a confirm, an app-wide prompt): the board takes no key. */
function dialogOpen(): boolean {
  return document.querySelector('[role="dialog"], [role="alertdialog"]') !== null;
}

interface Solved {
  ms: number;
  /** The record at this size and level after this game (null: not known — no answer yet). */
  record: number | null;
  improved: boolean;
}

/** How a game ended, as its finish tells the server. */
function endOf(state: SudokuState, outcome: SudokuEnd["outcome"]): SudokuEnd {
  return {
    outcome,
    mode: state.mode,
    mistakes: state.mistakes,
    hintsUsed: state.hinted.length,
    assisted: state.revealed,
  };
}

export function SudokuTable({
  game: incoming,
  api,
  kept = null,
  onExit,
  onLeave,
  onClosing,
}: {
  game: SudokuGame;
  api: Pick<ApiClient, "saveSudoku" | "finishSudoku" | "getSudoku">;
  /** Where a finish waits on the device for the server's answer (none: in memory only). */
  kept?: KeptFinishes | null;
  /** Back to the start screen (the game stays saved). */
  onExit: () => void;
  /** Out to the lobby. */
  onLeave: () => void;
  /** The screen is going: what it still sends the server, settling once that has landed (or failed). */
  onClosing?: (closing: Promise<void>) => void;
}): ReactNode {
  // An older api's game (or one saved before mistake checking) has none of its fields: defaults, so
  // it plays as a clean "check" game instead of breaking the board.
  const [game] = useState(() => withSudokuDefaults(incoming));
  const [server, setServer] = useState<SudokuGame>(game);
  /** The game as the server last had it — for the listeners, which outlive a render. */
  const serverRef = useRef(game);
  const board: Board = useMemo(
    () => ({ size: server.size, puzzle: server.puzzle, solution: server.solution }),
    [server.size, server.puzzle, server.solution],
  );
  const [state, setState] = useState<SudokuState>(game.state);
  const stateRef = useRef(state);
  const [selected, setSelected] = useState<number | null>(null);
  const [notesMode, setNotesMode] = useState(false);
  const [solved, setSolved] = useState<Solved | null>(
    game.finishedMs !== null ? { ms: game.finishedMs, record: null, improved: false } : null,
  );
  /** The 3rd mistake ended it ("check"): the play time it ended at. */
  const [lost, setLost] = useState<number | null>(null);
  /** The game is over — solved or lost: no move, no save. */
  const solvedRef = useRef(solved !== null);
  /** "free": the full-grid message, with how many cells are wrong (null: not shown). */
  const [fullWrong, setFullWrong] = useState<number | null>(null);
  const [away, setAway] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [gone, setGone] = useState(false);
  const clockRef = useRef<SudokuClock | null>(null);
  clockRef.current ??= new SudokuClock(game.state.elapsedMs);
  const clock = clockRef.current;
  const [shownMs, setShownMs] = useState(game.state.elapsedMs);
  const keyOf = (g: SudokuGame): SudokuRecordKey => `${g.size}-${g.level}` as SudokuRecordKey;

  const onClosingRef = useRef(onClosing);
  onClosingRef.current = onClosing;

  const syncRef = useRef<SudokuSync | null>(null);
  syncRef.current ??= new SudokuSync(
    api,
    game,
    (adopted) => {
      if (!adopted || adopted.finishedMs !== null || adopted.outcome === "lost") {
        setGone(true);
        return;
      }
      const theirs = withSudokuDefaults(adopted);
      // Another phone played on — or started a new game there: its board, its time, its undo
      // history — this phone carries on there.
      const replaced = theirs.puzzle !== serverRef.current.puzzle;
      serverRef.current = theirs;
      setServer(theirs);
      stateRef.current = theirs.state;
      setState(theirs.state);
      clock.set(theirs.state.elapsedMs);
      setShownMs(theirs.state.elapsedMs);
      setNotice(replaced ? fa.sudoku.replaced : fa.sudoku.adopted);
    },
    kept,
  );
  const sync = syncRef.current;

  // ── hidden / shown ────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const isAway = (): boolean =>
      document.visibilityState === "hidden" || !lifecycle.store.getState().appActive;
    let hidden: boolean | null = null;
    const check = (): void => {
      const now = isAway();
      if (now === hidden) return;
      const first = hidden === null;
      hidden = now;
      setAway(now);
      if (now) {
        clock.pause();
        // Opened while hidden: nothing to save yet.
        if (!first && !solvedRef.current) {
          sync.changed({ ...stateRef.current, elapsedMs: clock.elapsed() });
          void sync.flush(true);
        }
        return;
      }
      if (first) return;
      // Back: one read — another phone may have played on.
      void api
        .getSudoku()
        .then((o) => sync.adoptIfNewer(serverRef.current.slot === "daily" ? o.daily : o.classic))
        .catch(() => {});
      void sync.retryFinish().then((outcome) => {
        if (outcome?.kind === "done") {
          setSolved((s) =>
            s
              ? {
                  ...s,
                  record: outcome.response.records[keyOf(serverRef.current)],
                  improved: outcome.response.improved,
                }
              : s,
          );
        }
      });
    };
    document.addEventListener("visibilitychange", check);
    const off = lifecycle.subscribe(check);
    check();
    return () => {
      document.removeEventListener("visibilitychange", check);
      off();
      clock.pause();
      // Leaving: the moves since the last save and the time — or, solved, a finish still waiting
      // for its answer — go now (keepalive); the start screen waits for them before it reads.
      const closing = sync.close(
        solvedRef.current ? null : { ...stateRef.current, elapsedMs: clock.elapsed() },
      );
      sync.dispose();
      onClosingRef.current?.(closing);
    };
    // Mount-only: the listeners live exactly as long as this screen (the refs carry the rest).
  }, []);

  // ── the time: one repaint a second while on screen and unsolved, nothing otherwise ──────────────
  const running = solved === null && lost === null && !away && !gone;
  useEffect(() => {
    if (!running) {
      clock.pause();
      setShownMs(clock.elapsed());
      return;
    }
    clock.start();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = (): void => {
      const ms = clock.elapsed();
      setShownMs(ms);
      timer = setTimeout(tick, 1000 - (ms % 1000) + 5);
    };
    tick();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [running, clock]);

  // ── the board's size: measured on the room under the band (no timer — a resize is an event) ──────
  // THE BOARD AND THE PAD SIT HIGH (the operator, 2026-11-03: «move the board up a bit, so the numbers
  // and buttons sit higher»): they stack from the top of the room — the board right under the band,
  // the pad right under the board — and whatever height is left stays BELOW the pad. So the board's
  // side is what the room has once the pad and the board's margins are taken: never measured on a
  // padded box (the review's I1 — that handed the board 24 px it did not have, off the LEFT edge).
  const roomRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLSpanElement>(null);
  const [side, setSide] = useState(FALLBACK_SIDE);
  const [zoom, setZoom] = useState(1);
  useLayoutEffect(() => {
    const room = roomRef.current;
    const area = room?.querySelector<HTMLElement>(".sudoku-area");
    const pad = room?.querySelector<HTMLElement>(".sudoku-pad");
    if (!room || !area || !pad) return;
    const px = (v: string): number => parseFloat(v) || 0;
    const measure = (): void => {
      const m = getComputedStyle(area);
      const width = room.clientWidth - px(m.marginLeft) - px(m.marginRight);
      const height = room.clientHeight - pad.offsetHeight - px(m.marginTop) - px(m.marginBottom);
      const s = Math.floor(Math.min(width, height));
      setSide(s > 0 ? s : FALLBACK_SIDE);
      const caption = captionRef.current;
      const z = caption ? parseFloat(getComputedStyle(caption).fontSize) / CAPTION_PX : 1;
      setZoom(Number.isFinite(z) && z > 0 ? z : 1);
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(room);
    ro.observe(pad);
    return () => ro.disconnect();
  }, []);

  // ── moves ───────────────────────────────────────────────────────────────────────────────────────
  const finish = (next: SudokuState): void => {
    solvedRef.current = true;
    clock.pause();
    const ms = clock.elapsed();
    setShownMs(ms);
    setSolved({ ms, record: null, improved: false });
    void sync.finish(filledGrid(board, next), ms, endOf(next, "won")).then((outcome) => {
      if (outcome.kind === "done") {
        setSolved({
          ms,
          record: outcome.response.records[keyOf(serverRef.current)],
          improved: outcome.response.improved,
        });
      }
    });
  };

  /** The 3rd mistake: the game is over at once — the lose card; the loss goes as a finish does. */
  const lose = (next: SudokuState): void => {
    solvedRef.current = true;
    clock.pause();
    const ms = clock.elapsed();
    setShownMs(ms);
    setLost(ms);
    void sync.finish(filledGrid(board, next), ms, endOf(next, "lost"));
  };

  // A saved game that arrives already over (its finish never got through) — at its 3rd mistake, or
  // solved: finish it now (the 3rd mistake first: a lost game is never won).
  const arrived = useRef<"solved" | "lost" | null>(
    game.finishedMs !== null || game.outcome === "lost"
      ? null
      : isLost(game.state)
        ? "lost"
        : isSolved(board, game.state)
          ? "solved"
          : null,
  );
  useEffect(() => {
    const was = arrived.current;
    arrived.current = null;
    if (was === "solved") finish(stateRef.current);
    else if (was === "lost") lose(stateRef.current);
    // Mount-only.
  }, []);

  const apply = (move: SudokuState): void => {
    if (move === stateRef.current || solvedRef.current) return;
    // "free": the grid just filled with mistakes — the message, once per game (saved with it).
    const wrongNow = fullGridMistakes(board, move);
    const next = wrongNow > 0 ? { ...move, fullShown: true } : move;
    stateRef.current = next;
    setState(next);
    setNotice(null);
    if (isSolved(board, next)) {
      finish(next);
      return;
    }
    if (isLost(next)) {
      lose(next);
      return;
    }
    sync.changed({ ...next, elapsedMs: clock.elapsed() });
    if (wrongNow > 0) {
      setFullWrong(wrongNow);
      // Saved at once, not in 3 s: a closed app must not show the message again.
      void sync.flush(false);
    }
  };

  /** «نشانم بده»: the wrong cells red until each changes — once per game (the game is «assisted»). */
  const showWhere = (): void => {
    setFullWrong(null);
    const before = stateRef.current;
    apply(reveal(board, before));
    // Its one use (and the red cells) saved at once: a closed app must not give it back.
    if (stateRef.current !== before) void sync.flush(false);
  };

  const digit = (d: number): void => {
    if (selected === null) return;
    const s = stateRef.current;
    apply(notesMode ? toggleNote(board, s, selected, d) : placeDigit(board, s, selected, d));
  };

  /** A digit from its pad key or its keyboard key alike — never into a given or a hint's cell. */
  const pressDigit = (d: number): void => {
    if (editable(board, stateRef.current, selected)) digit(d);
  };

  const tool = (t: "notes" | "erase" | "undo" | "hint" | "reveal"): void => {
    const s = stateRef.current;
    if (t === "reveal") showWhere();
    else if (t === "notes") setNotesMode((on) => !on);
    else if (t === "erase") {
      if (selected !== null) apply(eraseCell(board, s, selected));
    } else if (t === "undo") apply(undoMove(board, s));
    else {
      const used = takeHint(board, s, selected);
      if (used.cell !== null) {
        setSelected(used.cell);
        apply(used.state);
      }
    }
  };

  // THE KEYBOARD (a desktop's), read by each key's PLACE (`event.code`) so every layout works — the
  // Persian one too, as «مار»'s steering: the digits 1–size (top row or numpad) do exactly what the
  // pad's digit keys do (a note in notes mode); the arrows move the selection; Backspace/Delete erase;
  // Ctrl+Z (⌘Z) undoes. A held digit counts ONCE — its auto-repeat must never toggle a note off
  // again. While a dialog is up, with any other modifier held, and for every other key: nothing
  // happens and nothing is prevented. Only while this screen is up (the listener goes with it).
  const keysRef = useRef({ pressDigit, tool, selected });
  keysRef.current = { pressDigit, tool, selected };
  useEffect(() => {
    const n = board.size;
    /** FOCUS FOLLOWS THE SELECTION (the grid pattern — `SudokuGrid`): after every key handled here
     *  the selected cell has the focus, so a focus ring can only ever be on it, and Space/Enter
     *  (left to the browser) click it, not a cell or a pad key the selection has left. Nothing
     *  selected: a pad key keeps no focus either. */
    const focusCell = (cell: number | null): void => {
      const room = roomRef.current;
      if (cell === null) {
        const active = document.activeElement;
        if (active instanceof HTMLElement && room?.contains(active)) active.blur();
        return;
      }
      room?.querySelector<HTMLElement>(`[data-cell="${cell}"]`)?.focus({ preventScroll: true });
    };
    const onKey = (event: KeyboardEvent): void => {
      if (dialogOpen()) return;
      const { pressDigit: press, tool: use, selected: at } = keysRef.current;
      const mod = event.ctrlKey || event.metaKey;
      if (event.code === "KeyZ" && mod && !event.altKey && !event.shiftKey) {
        event.preventDefault();
        use("undo");
        focusCell(at);
        return;
      }
      if (mod || event.altKey || event.shiftKey) return; // someone else's shortcut
      const d = keyDigit(event);
      if (d >= 1 && d <= n) {
        event.preventDefault();
        if (!event.repeat) press(d);
        focusCell(at);
        return;
      }
      if (event.code === "Backspace" || event.code === "Delete") {
        event.preventDefault();
        use("erase");
        focusCell(at);
        return;
      }
      const step =
        ARROWS.get(event.code) ?? (event.code.startsWith("Numpad") ? ARROWS.get(event.key) : null);
      if (!step) return;
      event.preventDefault();
      // Nothing selected yet: the first arrow selects the first cell.
      const to =
        at === null
          ? 0
          : ((Math.floor(at / n) + step[0] + n) % n) * n + (((at % n) + step[1] + n) % n);
      setSelected(to);
      focusCell(to);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [board.size]);

  // Two lines, as the «زمان» box beside it: which game over which puzzle — never one line that a
  // text zoom (or «متوسط» at 307 px) cuts to «سودوکو · ۹×۹ س…».
  const titleGame = server.slot === "daily" ? fa.sudoku.daily : fa.sudoku.title;
  const titlePuzzle = `${fa.sudoku.sizes[server.size]} ${fa.sudoku.levels[server.level]}`;

  return (
    <div className="sudoku-screen" data-testid="sudoku-table">
      <div className="sudoku-band">
        <button
          type="button"
          onClick={onExit}
          aria-label={fa.sudoku.back}
          data-testid="sudoku-exit"
          className={EXIT_BTN}
        >
          <GameIcon type="exit" size={20} />
        </button>
        <div className="sudoku-band__title" data-testid="sudoku-title">
          <span className="font-caption text-caption text-on-surface-variant">{titleGame}</span>
          <span className="sudoku-band__puzzle font-headline-md font-bold text-secondary">
            {titlePuzzle}
          </span>
        </div>
        <div className="sudoku-band__time" data-testid="sudoku-time">
          <span
            ref={captionRef}
            className="font-caption text-caption leading-none text-on-surface-variant"
          >
            {fa.sudoku.time}
          </span>
          <span className="font-body-lg text-body-lg font-bold leading-tight tabular-nums text-secondary">
            {faDigits(formatPlayTime(solved ? solved.ms : (lost ?? shownMs)))}
          </span>
        </div>
      </div>

      {notice ? (
        <div className="sudoku-notice" role="status" data-testid="sudoku-notice">
          {notice}
        </div>
      ) : null}

      <div ref={roomRef} className="sudoku-room">
        <div className="sudoku-area">
          <SudokuGrid
            board={board}
            state={state}
            selected={selected}
            side={side}
            zoom={zoom}
            onSelect={(cell) => setSelected(cell)}
          />
        </div>

        <SudokuPad
          board={board}
          state={state}
          notesMode={notesMode}
          canUndo={state.undo.length > 0}
          lockedSelected={selected !== null && isLocked(board, state, selected)}
          onDigit={pressDigit}
          onTool={tool}
        />
      </div>

      {fullWrong !== null && !solved && lost === null && !gone ? (
        <EndCardShell
          label={fa.sudoku.fullTitle}
          testId="sudoku-full"
          scrim="rgba(0, 0, 0, 0.45)"
          cardClassName="end-card"
          cardStyle={END_CARD}
          actions={
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Button variant="primary" onClick={showWhere} data-testid="sudoku-full-show">
                {fa.sudoku.showWhere}
              </Button>
              <Button variant="ghost" onClick={() => setFullWrong(null)}>
                {fa.sudoku.keepGoing}
              </Button>
            </div>
          }
        >
          <div style={{ fontSize: 40, lineHeight: 1.1 }} aria-hidden>
            🔍
          </div>
          <p style={{ margin: 0, fontSize: 17, fontWeight: 800 }} data-testid="sudoku-full-text">
            {fa.sudoku.fullWrong(fullWrong)}
          </p>
        </EndCardShell>
      ) : null}

      {lost !== null && !gone ? (
        <EndCardShell
          label={fa.sudoku.lost}
          testId="sudoku-lost"
          scrim="rgba(0, 0, 0, 0.45)"
          cardClassName="end-card"
          cardStyle={END_CARD}
          actions={
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Button variant="primary" onClick={onExit}>
                {fa.sudoku.again}
              </Button>
              <Button variant="ghost" onClick={onLeave}>
                {fa.sudoku.leave}
              </Button>
            </div>
          }
        >
          <div style={{ fontSize: 40, lineHeight: 1.1 }} aria-hidden>
            ❌
          </div>
          <h2 style={{ margin: "4px 0 6px", fontSize: 20, fontWeight: 800 }}>
            {fa.sudoku.lostTitle}
          </h2>
          {server.slot === "daily" ? (
            <p
              style={{ margin: 0, fontSize: 14, color: "var(--hint)" }}
              data-testid="sudoku-lost-daily"
            >
              {fa.sudoku.lostDaily}
            </p>
          ) : null}
          <div data-testid="sudoku-lost-time" style={{ marginTop: 8 }}>
            <div style={{ fontSize: 13, color: "var(--hint)" }}>{fa.sudoku.time}</div>
            <div className="tabular-nums" style={{ fontSize: 26, fontWeight: 800 }}>
              {faDigits(formatPlayTime(lost))}
            </div>
          </div>
        </EndCardShell>
      ) : null}

      {solved || gone ? (
        <EndCardShell
          label={gone ? fa.sudoku.gone : fa.sudoku.solved}
          testId={gone ? "sudoku-gone" : "sudoku-solved"}
          scrim="rgba(0, 0, 0, 0.45)"
          cardClassName={solved?.improved ? "end-card end-card--win" : "end-card"}
          cardStyle={END_CARD}
          actions={
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <Button variant="primary" onClick={onExit}>
                {gone ? fa.sudoku.back : fa.sudoku.again}
              </Button>
              <Button variant="ghost" onClick={onLeave}>
                {fa.sudoku.leave}
              </Button>
            </div>
          }
        >
          {gone ? (
            <p style={{ margin: 0, fontWeight: 700 }}>{fa.sudoku.gone}</p>
          ) : (
            <>
              <div style={{ fontSize: 40, lineHeight: 1.1 }} aria-hidden>
                {solved?.improved ? "🏆" : "🔢"}
              </div>
              <h2 style={{ margin: "4px 0 10px", fontSize: 20, fontWeight: 800 }}>
                {fa.sudoku.solved}
              </h2>
              <div style={{ display: "flex", justifyContent: "center", gap: 28 }}>
                <div data-testid="sudoku-end-time">
                  <div style={{ fontSize: 13, color: "var(--hint)" }}>{fa.sudoku.time}</div>
                  <div className="tabular-nums" style={{ fontSize: 26, fontWeight: 800 }}>
                    {faDigits(formatPlayTime(solved?.ms ?? 0))}
                  </div>
                </div>
                {solved?.record != null ? (
                  <div data-testid="sudoku-end-record">
                    <div style={{ fontSize: 13, color: "var(--hint)" }}>{fa.sudoku.record}</div>
                    <div className="tabular-nums" style={{ fontSize: 26, fontWeight: 800 }}>
                      {faDigits(formatPlayTime(solved.record))}
                    </div>
                  </div>
                ) : null}
              </div>
              {solved?.improved ? (
                <div
                  data-testid="sudoku-new-record"
                  className="text-secondary"
                  style={{ marginTop: 8, fontSize: 14, fontWeight: 700 }}
                >
                  {fa.sudoku.newRecord}
                </div>
              ) : null}
            </>
          )}
        </EndCardShell>
      ) : null}
    </div>
  );
}
